/**
 * Custom Add-to-Cart Button Synchronization Manager
 * Manages state synchronization across all custom add-to-cart buttons
 * (.product__custom-add-to-cart, .modal-add-to-cart, .sticky-add-to-cart)
 */

// Prevent duplicate class declarations
if (typeof window.CustomAddToCartSyncManager === 'undefined') {
  window.CustomAddToCartSyncManager = class CustomAddToCartSyncManager {
  constructor() {
    // Singleton guard: this file can be included multiple times (main/sticky/modal),
    // so avoid stacking listeners/observers which causes progressive lag.
    if (window.customAddToCartSyncManager) return window.customAddToCartSyncManager;

    this.buttons = new Map();
    this.currentState = {
      isLoading: false,
      isDisabled: true,
      buttonText: 'Select Products',
      isSubscription: false,
      selectedProducts: [],
      selectedCount: 0
    };

    this._listenersAttached = false;
    this._mutationObserver = null;
    this._rescanTimer = null;
    this._variantFlashTimer = null;

    // expose instance early so subsequent inits reuse it
    window.customAddToCartSyncManager = this;
    
    this.init();
  }

  init() {
    // Wait for DOM to be ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => {
        this.setupButtons();
        this.handleSubscriptionChange();
        this.handleProductSelectionChange(); // Initialize with current product selection state
        
        // Additional scan after a short delay to catch any late-rendered buttons
        setTimeout(() => {
          this.setupButtons();
          this.handleSubscriptionChange();
          this.handleProductSelectionChange();
        }, 200);
      });
    } else {
      this.setupButtons();
      this.handleSubscriptionChange();
      this.handleProductSelectionChange(); // Initialize with current product selection state
      
      // Additional scan after a short delay to catch any late-rendered buttons
      setTimeout(() => {
        this.setupButtons();
        this.handleSubscriptionChange();
        this.handleProductSelectionChange();
      }, 200);
    }

    // Setup event listeners
    this.setupEventListeners();
  }

  setupButtons() {
    // Find all custom add-to-cart buttons (including hidden ones)
    const buttonSelectors = [
      '.product__custom-add-to-cart .custom-add-to-cart-btn',
      '.modal-add-to-cart .custom-add-to-cart-btn',
      '.sticky-add-to-cart .custom-add-to-cart-btn'
    ];

    // Clear existing buttons to avoid duplicates
    this.buttons.clear();

    buttonSelectors.forEach(selector => {
      const buttons = document.querySelectorAll(selector);
      buttons.forEach((button, index) => {
        // Generate unique ID based on context and index to avoid conflicts
        const context = this.getButtonContext(button);
        const existingId = button.getAttribute('data-sync-id');
        const buttonId = existingId || `${context}-btn-${index}`;
        if (!existingId) button.setAttribute('data-sync-id', buttonId);
        
        // Only add if not already tracked
        if (!this.buttons.has(buttonId)) {
          const buttonData = {
            element: button,
            context: context,
            btnText: button.querySelector('.btn-text'),
            btnLoading: button.querySelector('.btn-loading')
          };
          this.buttons.set(buttonId, buttonData);
        }
      });
    });
    
    // Initial state update
    this.updateAllButtons();
  }

  getButtonContext(button) {
    if (button.closest('.modal-add-to-cart')) return 'modal';
    if (button.closest('.sticky-add-to-cart')) return 'sticky';
    return 'main';
  }

  setupEventListeners() {
    if (this._listenersAttached) return;
    this._listenersAttached = true;

    // Listen for subscription changes
    document.addEventListener('change', (e) => {
      if (e.target.classList.contains('subscription__checkbox')) {
        this.handleSubscriptionChange();
      }
      
      // Listen for subscription select changes
      if (e.target.classList.contains('subscription__select')) {
        this.handleSubscriptionSelectChange(e.target);
      }
    });

    // Listen for product selection changes
    document.addEventListener('productSelection:changed', () => {
      this.handleProductSelectionChange();
    });

    // Listen for cart updates
    document.addEventListener('cart:updated', () => {
      this.handleCartUpdate();
    });

    // iOS/Safari back-forward cache can restore "loading" CTAs from pre-checkout state.
    window.addEventListener('pageshow', () => {
      this.setLoadingState(false);
      this.setSuccessState(false);
      this.handleSubscriptionChange();
      this.handleProductSelectionChange();
      this.updateAllButtons();
    });

    // Clear variant flash when product selection modal closes (so reopening by title click doesn't show highlight)
    const productVariantsModal = document.getElementById('productVariantsModal');
    if (productVariantsModal) {
      productVariantsModal.addEventListener('close', () => this.clearVariantFlash());
    }

    // Setup mutation observer for dynamically added buttons
    this.setupMutationObserver();
  }

  clearVariantFlash() {
    if (this._variantFlashTimer) {
      window.clearTimeout(this._variantFlashTimer);
      this._variantFlashTimer = null;
    }
    const modal = document.getElementById('productVariantsModal');
    const scope = modal || document;
    scope.querySelectorAll('.product-variant-card.cs-variant-flash').forEach((c) => c.classList.remove('cs-variant-flash'));
    scope.querySelectorAll('.product-variant-card .add-to-cart-btn.cs-variant-flash').forEach((b) => b.classList.remove('cs-variant-flash'));
  }

  flashVariantSelection(durationMs = 2600) {
    // Highlight variant cards/buttons briefly to guide the user.
    const modal = document.getElementById('productVariantsModal');
    const scope = modal || document;
    const cards = Array.from(scope.querySelectorAll('.product-variant-card'));
    if (cards.length === 0) return;

    const buttons = Array.from(scope.querySelectorAll('.product-variant-card .add-to-cart-btn'));

    cards.forEach((c) => c.classList.add('cs-variant-flash'));
    buttons.forEach((b) => b.classList.add('cs-variant-flash'));

    if (this._variantFlashTimer) window.clearTimeout(this._variantFlashTimer);
    this._variantFlashTimer = window.setTimeout(() => {
      cards.forEach((c) => c.classList.remove('cs-variant-flash'));
      buttons.forEach((b) => b.classList.remove('cs-variant-flash'));
      this._variantFlashTimer = null;
    }, durationMs);
  }

  setupMutationObserver() {
    if (this._mutationObserver) return;

    const scheduleRescan = () => {
      if (this._rescanTimer) window.clearTimeout(this._rescanTimer);
      this._rescanTimer = window.setTimeout(() => {
        this.setupButtons();
        this.handleProductSelectionChange();
      }, 200);
    };

    this._mutationObserver = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        for (const node of mutation.addedNodes) {
          if (!node || node.nodeType !== Node.ELEMENT_NODE) continue;
          const el = /** @type {Element} */ (node);
          if (
            (typeof el.matches === 'function' && el.matches('.custom-add-to-cart-btn')) ||
            (typeof el.querySelector === 'function' && el.querySelector('.custom-add-to-cart-btn'))
          ) {
            scheduleRescan();
            return;
          }
        }
      }
    });

    this._mutationObserver.observe(document.body, {
      childList: true,
      subtree: true
    });
  }
  handleSubscriptionChange() {
    const buyOnceCheckboxes = document.querySelectorAll('.subscription__checkbox[data-type="buy-once"]');
    const subscribeCheckboxes = document.querySelectorAll('.subscription__checkbox[data-type="subscribe"]');
    const selectWrappers = document.querySelectorAll('.subscription__select-wrapper');

    // Determine current state
    const isSubscribe = Array.from(subscribeCheckboxes).some(cb => cb.checked);
    const isBuyOnce = Array.from(buyOnceCheckboxes).some(cb => cb.checked);

    // Enforce mutual exclusivity
    if (isSubscribe) {
      buyOnceCheckboxes.forEach(cb => (cb.checked = false));
    } else if (isBuyOnce) {
      subscribeCheckboxes.forEach(cb => (cb.checked = false));
    } else {
      // If none selected, default to Buy Once
      buyOnceCheckboxes.forEach(cb => (cb.checked = true));
    }

    // Update dropdown visibility
    selectWrappers.forEach(wrapper => {
      wrapper.style.display = isSubscribe ? 'block' : 'none';
      wrapper.classList.toggle('show', isSubscribe);
    });

    // Update global state
    this.currentState.isSubscription = isSubscribe;
    this.updateButtonText();
    this.updateAllButtons();
  }

  handleSubscriptionSelectChange(changedSelect) {
    // Sync all subscription selects to same value
    const allSelects = document.querySelectorAll('.subscription__select');
    const value = changedSelect.value;
    allSelects.forEach(select => {
      if (select !== changedSelect) select.value = value;
    });

    this.updateButtonText();
    this.updateAllButtons();
  }


  handleProductSelectionChange() {
    let selectedProducts = [];
    const hasVariantGrid = this.hasVariantGridSelections();
    if (hasVariantGrid) {
      selectedProducts = this.getVariantGridSelectedProducts();
    } else if (window.productSelectionManager && window.productSelectionManager.getSelectedProducts) {
      selectedProducts = window.productSelectionManager.getSelectedProducts();
    }

    if (!selectedProducts.length && window.productSelectionManager && window.productSelectionManager.getSelectedProducts) {
      selectedProducts = window.productSelectionManager.getSelectedProducts();
    }

    const uiSelectedCount = this.getSelectedCountFromUI();
    const selectedCount = Math.max(selectedProducts.length, uiSelectedCount);
    this.currentState.selectedProducts = selectedProducts;
    this.currentState.selectedCount = selectedCount;
    this.currentState.isDisabled = selectedCount === 0;
    
    // Always call updateButtonText to handle both product selection and subscription state
    this.updateButtonText();
    this.updateAllButtons();
  }

  handleCartUpdate() {
    // Reset loading state after cart update
    if (this.currentState.isLoading) {
      this.setLoadingState(false);
      this.setSuccessState(true);
      
      setTimeout(() => {
        this.setSuccessState(false);
        this.updateAllButtons();
      }, 2000);
    }
  }

  updateButtonText() {
    const preorderActive = this.hasPreorderSelection() || this.hasPreorderButtonContext();

    // If no products selected, show select products message
    if ((this.currentState.selectedCount || 0) === 0) {
      this.currentState.buttonText = preorderActive ? 'Preorder' : 'Select products';
      return;
    }

    // Variant-card ATC flow: once at least one product is selected, proceed to cart.
    this.currentState.buttonText = preorderActive ? 'Preorder' : 'Proceed to cart';
  }

  setLoadingState(isLoading) {
    this.currentState.isLoading = isLoading;
    this.updateAllButtons();
  }

  setSuccessState(isSuccess) {
    this.buttons.forEach((buttonData) => {
      const { element, btnText } = buttonData;
      if (isSuccess) {
        element.classList.add('success');
        if (btnText) btnText.textContent = 'Added!';
      } else {
        element.classList.remove('success');
      }
    });
  }

  updateAllButtons() {
    this.buttons.forEach((buttonData, buttonId) => {
      this.updateButton(buttonId);
    });
  }

  updateButton(buttonId) {
    const buttonData = this.buttons.get(buttonId);
    if (!buttonData) return;

    const { element, btnText, btnLoading, context } = buttonData;
    if (!element || !btnText) return;
    const isWaitlistMode = String(element.getAttribute('data-waitlist-mode') || '').toLowerCase() === 'true';

    if (isWaitlistMode) {
      element.classList.remove('disabled');
      element.removeAttribute('data-disabled');
      element.classList.remove('loading');
      if (btnText) {
        btnText.style.display = 'block';
        btnText.textContent = element.getAttribute('data-waitlist-button-text') || 'Join waitlist';
      }
      if (btnLoading) btnLoading.style.display = 'none';
      return;
    }

    // Update disabled state
    if (this.currentState.isDisabled) {
      element.classList.add('disabled');
      element.setAttribute('data-disabled', 'true');
    } else {
      element.classList.remove('disabled');
      element.removeAttribute('data-disabled');
    }

    // Update loading state
    if (this.currentState.isLoading) {
      element.classList.add('loading');
      if (btnText) btnText.style.display = 'none';
      if (btnLoading) btnLoading.style.display = 'flex';
    } else {
      element.classList.remove('loading');
      if (btnText) btnText.style.display = 'block';
      if (btnLoading) btnLoading.style.display = 'none';
    }

    // Update button text based on context
    if (btnText && !this.currentState.isLoading) {
      let displayText = this.currentState.buttonText;
      const isPreorderContext = this.isPreorderContextButton(element) || this.hasPreorderSelection();
      if (isPreorderContext) {
        displayText = 'Preorder';
      } else {
        // Keep CTA text deterministic across main/sticky/modal contexts.
        displayText = this.currentState.isDisabled ? 'Select products' : 'Proceed to cart';
      }
      
      btnText.textContent = displayText;
    }
  }

  // Public method to handle add to cart action
  handleAddToCart(button, sectionId) {
    if (button && String(button.getAttribute('data-waitlist-mode') || '').toLowerCase() === 'true') {
      if (typeof window.openWaitlistModal === 'function') window.openWaitlistModal(sectionId);
      return;
    }
    // Prevent double clicks
    if (this.currentState.isLoading) return;

    // Check if products are selected
    if ((this.currentState.selectedCount || 0) === 0) {
      if (window.productSelectionManager && window.productSelectionManager.openProductSelection) {
        // Find the first available slot
        const quantityOptions = document.querySelectorAll('.quantity-option');
        let availableSlot = null;

        for (let i = 0; i < quantityOptions.length; i++) {
          const card = quantityOptions[i];
          const hasProduct = card.querySelector('.quantity-option__product-info');
          if (!hasProduct) {
            availableSlot = i + 1;
            break;
          }
        }

        if (availableSlot) {
          window.productSelectionManager.openProductSelection(availableSlot);
          // Only highlight when the disabled custom-add-to-cart btn inside the product variants modal was clicked (not main/sticky CTA or when modal opened by title)
          const isModalCta = button && button.closest('#productVariantsModal') && button.closest('.product__custom-add-to-cart');
          const clickedDisabledModalCta = isModalCta && (button.classList.contains('disabled') || button.getAttribute('data-disabled') === 'true');
          if (clickedDisabledModalCta) {
            window.setTimeout(() => this.flashVariantSelection(2600), 120);
          }
        } else {
          alert('All product slots are filled. Please clear a slot to add more products.');
        }
      } else {
        alert('Please select at least one product.');
      }
      return;
    }



    // Check subscription state (subscribe checkbox only)
    const subscriptionCheckboxes = document.querySelectorAll('.subscription__checkbox[data-sync-group="subscription"][data-type="subscribe"]');
    const subscriptionSelects = document.querySelectorAll('.subscription__select');
    const isSubscription = Array.from(subscriptionCheckboxes).some((checkbox) => checkbox.checked);

    let sellingPlanId = null;
    if (isSubscription) {
      // Get selling plan from any select that has a value (fallback to first option)
      for (const select of subscriptionSelects) {
        if (select.value) {
          sellingPlanId = parseInt(select.value, 10);
          break;
        }
      }

      if (!sellingPlanId && subscriptionSelects[0] && subscriptionSelects[0].options && subscriptionSelects[0].options.length) {
        sellingPlanId = parseInt(subscriptionSelects[0].options[0].value, 10);
      }

      // Sync all selects to the same value
      if (sellingPlanId) {
        subscriptionSelects.forEach((select) => {
          select.value = sellingPlanId.toString();
        });
      }
    }

    // Set loading state
    this.setLoadingState(true);

    // Prepare cart data
    const cartData = {
      items: this.getItemsForCartPayload(sellingPlanId),
    };

    if (!cartData.items.length) {
      this.setLoadingState(false);
      this.updateAllButtons();
      return;
    }

    const cartDrawer = document.querySelector('cart-drawer');
    const shouldFlushPendingCartDrawerWork =
      cartDrawer &&
      typeof cartDrawer.flushAllPendingNow === 'function' &&
      ((typeof cartDrawer.hasPendingCartMutations === 'function' && cartDrawer.hasPendingCartMutations()) ||
        (typeof cartDrawer.hasPendingGroupedWork === 'function' && cartDrawer.hasPendingGroupedWork()) ||
        (cartDrawer._pendingMutations && cartDrawer._pendingMutations.size));
    const preflight = shouldFlushPendingCartDrawerWork
      ? cartDrawer.flushAllPendingNow().catch(() => {})
      : Promise.resolve();

    // Add to cart via AJAX
    preflight.then(() =>
      fetch(window.Shopify.routes.root + 'cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(cartData),
      })
    )
      .then((response) => response.json())
      .then((data) => {


        // Trigger cart update events
        document.dispatchEvent(new CustomEvent('cart:updated'));

        // Show upsell modal after successful cart addition.
        // Use both global paths and defer one frame for stable post-cart render.
        const upsellOpener =
          (typeof window !== 'undefined' && typeof window.showUpsellModal === 'function' && window.showUpsellModal) ||
          (typeof showUpsellModal === 'function' && showUpsellModal) ||
          null;
        if (upsellOpener) requestAnimationFrame(() => upsellOpener());
      })
      .catch((error) => {
        console.error('Error:', error);

        // Reset loading state on error
        this.setLoadingState(false);
        this.updateAllButtons();

        // Show error message
        alert('Error adding item to cart. Please try again.');
      });
  }


  handleAddToCart2(button, sectionId) {
    if (button && String(button.getAttribute('data-waitlist-mode') || '').toLowerCase() === 'true') {
      if (typeof window.openWaitlistModal === 'function') window.openWaitlistModal(sectionId);
      return;
    }
    // Prevent double clicks
    if (this.currentState.isLoading) return;

    // Check if products are selected
    if ((this.currentState.selectedCount || 0) === 0) {
      if (window.productSelectionManager && window.productSelectionManager.openProductSelection) {
        // Find the first available slot
        const quantityOptions = document.querySelectorAll('.quantity-option');
        let availableSlot = null;

        for (let i = 0; i < quantityOptions.length; i++) {
          const card = quantityOptions[i];
          const hasProduct = card.querySelector('.quantity-option__product-info');
          if (!hasProduct) {
            availableSlot = i + 1;
            break;
          }
        }

        if (availableSlot) {
          window.productSelectionManager.openProductSelection(availableSlot);
          // Only highlight when the disabled custom-add-to-cart btn inside the product variants modal was clicked (not main/sticky CTA or when modal opened by title)
          const isModalCta = button && button.closest('#productVariantsModal') && button.closest('.product__custom-add-to-cart');
          const clickedDisabledModalCta = isModalCta && (button.classList.contains('disabled') || button.getAttribute('data-disabled') === 'true');
          if (clickedDisabledModalCta) {
            window.setTimeout(() => this.flashVariantSelection(2600), 120);
          }
        } else {
          alert('All product slots are filled. Please clear a slot to add more products.');
        }
      } else {
        alert('Please select at least one product.');
      }
      return;
    }



    // Check subscription state (subscribe checkbox only)
    const subscriptionCheckboxes = document.querySelectorAll('.subscription__checkbox[data-sync-group="subscription"][data-type="subscribe"]');
    const subscriptionSelects = document.querySelectorAll('.subscription__select');
    const isSubscription = Array.from(subscriptionCheckboxes).some((checkbox) => checkbox.checked);

    let sellingPlanId = null;
    if (isSubscription) {
      // Get selling plan from any select that has a value (fallback to first option)
      for (const select of subscriptionSelects) {
        if (select.value) {
          sellingPlanId = parseInt(select.value, 10);
          break;
        }
      }

      if (!sellingPlanId && subscriptionSelects[0] && subscriptionSelects[0].options && subscriptionSelects[0].options.length) {
        sellingPlanId = parseInt(subscriptionSelects[0].options[0].value, 10);
      }

      // Sync all selects to the same value
      if (sellingPlanId) {
        subscriptionSelects.forEach((select) => {
          select.value = sellingPlanId.toString();
        });
      }
    }

    // Set loading state
    this.setLoadingState(true);

    // Prepare cart data
    const cartData = {
      items: this.currentState.selectedProducts.map((product) => {
        const item = {
          id: parseInt(product.id),
          quantity: 1,
        };

        if (sellingPlanId) {
          item.selling_plan = sellingPlanId;
        }

        if (this.resolvePreorderForProduct(product)) {
          item.properties = { Preorder: 'This item is a preorder' };
        }

        return item;
      }),
    };

  
       // Show upsell modal after successful cart addition
        const upsellOpener =
          (typeof window !== 'undefined' && typeof window.showUpsellModal === 'function' && window.showUpsellModal) ||
          (typeof showUpsellModal === 'function' && showUpsellModal) ||
          null;
        if (upsellOpener) requestAnimationFrame(() => upsellOpener());





  }



  // Public method to get current state
  getCurrentState() {
    return { ...this.currentState };
  }

  getSelectedCountFromUI() {
    const inputs = Array.from(document.querySelectorAll('.product-variant-card[data-variant-id] .quantity-input'));

    const qtyByVariant = new Map();
    inputs.forEach((input) => {
      const card = input.closest('.product-variant-card[data-variant-id]');
      const variantId = String(card?.dataset?.variantId || '').trim();
      if (!variantId) return;
      const qty = Math.max(0, parseInt(input.value || '0', 10) || 0);
      const prev = qtyByVariant.get(variantId) || 0;
      if (qty > prev) qtyByVariant.set(variantId, qty);
    });

    let total = 0;
    qtyByVariant.forEach((qty) => {
      total += qty;
    });

    const gridCards = Array.from(
      document.querySelectorAll('variant-selects[data-cs-variant-grid-atc="true"] .cs-variant-grid-atc[data-variant-id]')
    );
    const parseSubQtyMap = (raw) => {
      const text = String(raw || '').trim();
      if (!text) return {};
      try {
        const parsed = JSON.parse(text);
        const result = {};
        Object.keys(parsed || {}).forEach((planId) => {
          const key = String(planId || '').trim();
          if (!key) return;
          const qty = Math.max(0, parseInt(parsed[planId], 10) || 0);
          if (qty > 0) result[key] = qty;
        });
        return result;
      } catch (_) {
        return {};
      }
    };
    gridCards.forEach((card) => {
      const onceQty = Math.max(0, parseInt(card.getAttribute('data-cs-qty-once') || '0', 10) || 0);
      const subMap = parseSubQtyMap(card.getAttribute('data-cs-qty-sub-map'));
      const subTotal = Object.values(subMap).reduce((sum, qty) => sum + (Math.max(0, parseInt(qty, 10) || 0)), 0);
      if (onceQty || subTotal) {
        total += onceQty + subTotal;
        return;
      }
      const fallbackVisibleQty = Math.max(
        0,
        parseInt(card.querySelector('.cs-variant-grid-atc__qty-input')?.value || '0', 10) || 0
      );
      total += fallbackVisibleQty;
    });

    return total;
  }

  getItemsForCartPayload(sellingPlanId) {
    if (Array.isArray(this.currentState.selectedProducts) && this.currentState.selectedProducts.length) {
      return this.normalizeSelectedProductsForPayload(this.currentState.selectedProducts, sellingPlanId);
    }

    const gridItems = this.readVariantGridItemsFromDOM(sellingPlanId);
    if (gridItems.length) {
      return gridItems;
    }

    // Fallback to current UI quantities if selectedProducts is temporarily stale.
    const inputs = Array.from(document.querySelectorAll('.product-variant-card[data-variant-id] .quantity-input'));
    const qtyByVariant = new Map();
    inputs.forEach((input) => {
      const card = input.closest('.product-variant-card[data-variant-id]');
      const variantId = String(card?.dataset?.variantId || '').trim();
      if (!variantId) return;
      const qty = Math.max(0, parseInt(input.value || '0', 10) || 0);
      const prev = qtyByVariant.get(variantId) || 0;
      if (qty > prev) qtyByVariant.set(variantId, qty);
    });

    const items = [];
    qtyByVariant.forEach((qty, variantId) => {
      if (qty <= 0) return;
      const item = {
        id: parseInt(variantId, 10),
        quantity: qty,
      };
      if (sellingPlanId) item.selling_plan = sellingPlanId;
      if (this.resolvePreorderForVariantId(variantId)) {
        item.properties = { Preorder: 'This item is a preorder' };
      }
      items.push(item);
    });
    return items;
  }

  hasVariantGridSelections() {
    return Boolean(
      document.querySelector('variant-selects[data-cs-variant-grid-atc="true"] .cs-variant-grid-atc[data-variant-id]')
    );
  }

  getVariantGridSelectedProducts() {
    const store = window.__csVariantGridSelection;
    if (!store || !Array.isArray(store.selectedProducts)) return [];
    return store.selectedProducts;
  }

  normalizeSelectedProductsForPayload(selectedProducts, sellingPlanId) {
    const grouped = new Map();
    (selectedProducts || []).forEach((product) => {
      const variantId = parseInt(product?.id, 10);
      if (!variantId) return;

      const productPlanIdRaw =
        product?.sellingPlanId != null && String(product.sellingPlanId).trim()
          ? String(product.sellingPlanId).trim()
          : product?.selling_plan != null && String(product.selling_plan).trim()
            ? String(product.selling_plan).trim()
            : '';
      const effectivePlanId = productPlanIdRaw || (sellingPlanId ? String(sellingPlanId) : '');
      const isPreorder = this.resolvePreorderForProduct(product) || this.resolvePreorderForVariantId(variantId);
      const key = `${variantId}::${effectivePlanId}::${isPreorder ? 'preorder' : 'regular'}`;
      const prev = grouped.get(key) || { id: variantId, quantity: 0, selling_plan: effectivePlanId, preorder: isPreorder };
      prev.quantity += Math.max(1, parseInt(product?.quantity, 10) || 1);
      grouped.set(key, prev);
    });

    return Array.from(grouped.values()).map((entry) => {
      const item = { id: entry.id, quantity: entry.quantity };
      if (entry.selling_plan) item.selling_plan = parseInt(entry.selling_plan, 10);
      if (entry.preorder) item.properties = { Preorder: 'This item is a preorder' };
      return item;
    });
  }

  readVariantGridItemsFromDOM(sellingPlanId) {
    const cards = Array.from(
      document.querySelectorAll('variant-selects[data-cs-variant-grid-atc="true"] .cs-variant-grid-atc[data-variant-id]')
    );
    if (!cards.length) return [];

    const grouped = new Map();
    const parseSubQtyMap = (raw) => {
      const text = String(raw || '').trim();
      if (!text) return {};
      try {
        const parsed = JSON.parse(text);
        const result = {};
        Object.keys(parsed || {}).forEach((planId) => {
          const key = String(planId || '').trim();
          if (!key) return;
          const qty = Math.max(0, parseInt(parsed[planId], 10) || 0);
          if (qty > 0) result[key] = qty;
        });
        return result;
      } catch (_) {
        return {};
      }
    };
    cards.forEach((card) => {
      const variantId = parseInt(card.getAttribute('data-variant-id') || '0', 10);
      if (!variantId) return;
      const onceQty = Math.max(0, parseInt(card.getAttribute('data-cs-qty-once') || '0', 10) || 0);
      const visibleQty = Math.max(0, parseInt(card.querySelector('.cs-variant-grid-atc__qty-input')?.value || '0', 10) || 0);
      const subMap = parseSubQtyMap(card.getAttribute('data-cs-qty-sub-map'));
      const isPreorder = String(card.getAttribute('data-cs-preorder') || '').toLowerCase() === 'true';

      const planSelect = card.querySelector('.cs-variant-grid-atc__plan');
      const selectedPlan = String(planSelect?.value || '').trim();
      const lastPlan = String(card.getAttribute('data-cs-last-subscription-plan-id') || '').trim();
      const fallbackPlanId = selectedPlan || lastPlan || (sellingPlanId ? String(sellingPlanId) : '');

      const subTotal = Object.values(subMap).reduce((sum, qty) => sum + (Math.max(0, parseInt(qty, 10) || 0)), 0);
      const effectiveOnceQty = onceQty || (!subTotal && !selectedPlan ? visibleQty : 0);
      if (effectiveOnceQty > 0) {
        const onceKey = `${variantId}::::${isPreorder ? 'preorder' : 'regular'}`;
        const oncePrev = grouped.get(onceKey) || { id: variantId, quantity: 0, selling_plan: '', preorder: isPreorder };
        oncePrev.quantity += effectiveOnceQty;
        grouped.set(onceKey, oncePrev);
      }

      const planIds = Object.keys(subMap);
      if (!planIds.length && fallbackPlanId && subTotal > 0) {
        planIds.push(fallbackPlanId);
      }
      planIds.forEach((planId) => {
        const qty = Math.max(0, parseInt(subMap[planId], 10) || 0);
        if (!planId || !qty) return;
        const subKey = `${variantId}::${planId}::${isPreorder ? 'preorder' : 'regular'}`;
        const subPrev = grouped.get(subKey) || { id: variantId, quantity: 0, selling_plan: planId, preorder: isPreorder };
        subPrev.quantity += qty;
        grouped.set(subKey, subPrev);
      });

      if (!Object.keys(subMap).length && subTotal > 0 && fallbackPlanId) {
        const subKey = `${variantId}::${fallbackPlanId}::${isPreorder ? 'preorder' : 'regular'}`;
        const subPrev = grouped.get(subKey) || { id: variantId, quantity: 0, selling_plan: fallbackPlanId, preorder: isPreorder };
        subPrev.quantity += subTotal;
        grouped.set(subKey, subPrev);
      }
    });

    return Array.from(grouped.values()).map((entry) => {
      const item = { id: entry.id, quantity: entry.quantity };
      if (entry.selling_plan) item.selling_plan = parseInt(entry.selling_plan, 10);
      if (entry.preorder) item.properties = { Preorder: 'This item is a preorder' };
      return item;
    });
  }

  resolvePreorderForProduct(product) {
    if (!product || typeof product !== 'object') return false;
    if (product.preorderStatus === true) return true;
    if (product.isPreorder === true) return true;
    if (String(product.preorderStatus || '').toLowerCase() === 'true') return true;
    return false;
  }

  resolvePreorderForVariantId(variantId) {
    const id = String(parseInt(variantId, 10) || '').trim();
    if (!id) return false;

    const selectors = [
      `.product-variant-card[data-variant-id="${id}"]`,
      `.upsell-modal__product[data-variant-id="${id}"]`,
      `.cs-variant-grid-atc[data-variant-id="${id}"]`,
    ];
    for (const selector of selectors) {
      const el = document.querySelector(selector);
      if (!el) continue;
      if (String(el.getAttribute('data-preorder-status') || '').toLowerCase() === 'true') return true;
      if (String(el.getAttribute('data-cs-preorder') || '').toLowerCase() === 'true') return true;
    }
    return false;
  }

  hasPreorderSelection() {
    const selected = Array.isArray(this.currentState.selectedProducts) ? this.currentState.selectedProducts : [];
    for (const product of selected) {
      if (this.resolvePreorderForProduct(product)) return true;
      const id = product?.id;
      if (id && this.resolvePreorderForVariantId(id)) return true;
    }
    return false;
  }

  isPreorderContextButton(buttonEl) {
    if (!buttonEl) return false;
    return String(buttonEl.getAttribute('data-preorder-status') || '').toLowerCase() === 'true';
  }

  hasPreorderButtonContext() {
    for (const buttonData of this.buttons.values()) {
      if (this.isPreorderContextButton(buttonData?.element)) return true;
    }
    return false;
  }

  // Public method to force update all buttons
  forceUpdate() {
    this.setupButtons();
    this.handleProductSelectionChange(); // Ensure current state is applied
    this.updateAllButtons();
  }
}



  // Initialize the sync manager
  window.customAddToCartSyncManager = new window.CustomAddToCartSyncManager();

  // Global function for backward compatibility
  window.handleCustomAddToCart = function(button, sectionId) {
    if (window.customAddToCartSyncManager) {
      window.customAddToCartSyncManager.handleAddToCart(button, sectionId);
    }
  };


    window.handleCustomAddToCart2 = function(button, sectionId) {
    if (window.customAddToCartSyncManager) {
      window.customAddToCartSyncManager.handleAddToCart2(button, sectionId);
    }
  };


  // Global function to update button states
  window.updateCustomButtonState = function() {
    if (window.customAddToCartSyncManager) {
      window.customAddToCartSyncManager.handleProductSelectionChange();
    }
  };


}

// If already defined, just reinitialize
else if (window.CustomAddToCartSyncManager && !window.customAddToCartSyncManager) {
  window.customAddToCartSyncManager = new window.CustomAddToCartSyncManager();
}